import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useClient } from '../../context/ClientContext';
import { subscriptionService } from '../../services/subscriptionService';
import { stripeConnectService } from '../../services/stripeConnectService';
import BillingPlanCard from '../../components/settings/BillingPlanCard';
import BillingSubscriptionCard from '../../components/settings/BillingSubscriptionCard';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiCreditCard,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiZap,
  FiUsers,
  FiArrowRight,
  FiLink,
  FiExternalLink
} = FiIcons;

const normalizeRole = (role) =>
  String(role || '')
    .toLowerCase()
    .trim();

const canManageBilling = (role) => {
  const normalizedRole =
    normalizeRole(role);

  return (
    normalizedRole === 'owner' ||
    normalizedRole === 'owner_member'
  );
};

const getStatusLabel = (status) => {
  const normalized =
    String(status || '').toLowerCase();

  const labels = {
    active: 'Active',
    trialing: 'Trial',
    incomplete: 'Checkout Pending',
    past_due: 'Payment Past Due',
    unpaid: 'Payment Unpaid',
    paused: 'Paused',
    canceled: 'Canceled'
  };

  return (
    labels[normalized] ||
    'No Active Subscription'
  );
};

const getStatusClasses = (status) => {
  const normalized =
    String(status || '').toLowerCase();

  if (
    normalized === 'active' ||
    normalized === 'trialing'
  ) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (
    normalized === 'past_due' ||
    normalized === 'unpaid'
  ) {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }

  if (normalized === 'paused') {
    return 'border-orange-200 bg-orange-50 text-orange-700';
  }

  if (normalized === 'incomplete') {
    return 'border-blue-200 bg-blue-50 text-blue-700';
  }

  return 'border-slate-200 bg-slate-100 text-slate-600';
};

const getConnectStatusLabel = (
  account
) => {
  if (!account) {
    return 'Not connected';
  }

  if (
    account.onboarding_complete ||
    (
      account.details_submitted &&
      account.charges_enabled &&
      account.payouts_enabled
    )
  ) {
    return 'Connected';
  }

  if (account.details_submitted) {
    return 'Verification pending';
  }

  return 'Setup required';
};

const getConnectStatusClasses = (
  account
) => {
  const status =
    getConnectStatusLabel(account);

  if (status === 'Connected') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (
    status === 'Verification pending'
  ) {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }

  return 'border-slate-200 bg-slate-50 text-slate-600';
};

export default function Billing() {
  const location = useLocation();
  const navigate = useNavigate();

  const { activeClient } = useClient();

  /*
   * ---------------------------------------------------------
   * ORGANIZATION CONTEXT
   * ---------------------------------------------------------
   *
   * activeClient.id
   *   = client organization
   *
   * activeClient.account_id
   *   = managing accounting firm / practice
   *
   * Subscription billing and Stripe Connect belong
   * to the managing organization.
   */
  const organizationId =
    activeClient?.account_id;

  const currency =
    activeClient?.billingCurrency ||
    'USD';

  const canManage =
    canManageBilling(
      activeClient?.role
    );

  /*
   * ---------------------------------------------------------
   * SUBSCRIPTION STATE
   * ---------------------------------------------------------
   */
  const [plans, setPlans] =
    useState([]);

  const [
    subscription,
    setSubscription
  ] = useState(null);

  const [
    billingInterval,
    setBillingInterval
  ] = useState('month');

  const [loading, setLoading] =
    useState(true);

  const [
    checkoutPlanId,
    setCheckoutPlanId
  ] = useState(null);

  const [
    refreshing,
    setRefreshing
  ] = useState(false);

  const [error, setError] =
    useState(null);

  const [notice, setNotice] =
    useState(null);

  /*
   * ---------------------------------------------------------
   * STRIPE CONNECT STATE
   * ---------------------------------------------------------
   */
  const [
    connectAccount,
    setConnectAccount
  ] = useState(null);

  const [
    connectLoading,
    setConnectLoading
  ] = useState(false);

  const [
    connectRefreshing,
    setConnectRefreshing
  ] = useState(false);

  const [
    connectError,
    setConnectError
  ] = useState(null);

  /*
   * ---------------------------------------------------------
   * LOAD SUBSCRIPTION BILLING
   * ---------------------------------------------------------
   */
  const loadBilling = useCallback(
    async (
      showRefreshState = false
    ) => {
      if (!organizationId) {
        setPlans([]);
        setSubscription(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        if (showRefreshState) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError(null);

        const [
          planData,
          subscriptionData
        ] = await Promise.all([
          subscriptionService
            .getSubscriptionPlans(),

          subscriptionService
            .getOrganizationSubscription(
              organizationId
            )
        ]);

        setPlans(
          planData || []
        );

        setSubscription(
          subscriptionData || null
        );
      } catch (loadError) {
        console.error(
          'Billing load error:',
          loadError
        );

        setError(
          loadError?.message ||
            'Unable to load billing information.'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [organizationId]
  );

  /*
   * ---------------------------------------------------------
   * LOAD STRIPE CONNECT ACCOUNT
   * ---------------------------------------------------------
   */
  const loadConnectAccount =
    useCallback(
      async (
        showRefreshState = false
      ) => {
        if (!organizationId) {
          setConnectAccount(null);
          setConnectRefreshing(false);
          return;
        }

        try {
          if (showRefreshState) {
            setConnectRefreshing(true);
          }

          setConnectError(null);

          const data =
            await stripeConnectService
              .getConnectAccount(
                organizationId
              );

          setConnectAccount(
            data?.account ||
              data ||
              null
          );
        } catch (
          connectLoadError
        ) {
          console.error(
            'Stripe Connect load error:',
            connectLoadError
          );

          setConnectError(
            connectLoadError?.message ||
              'Unable to load Stripe Connect status.'
          );
        } finally {
          setConnectRefreshing(false);
        }
      },
      [organizationId]
    );

  /*
   * ---------------------------------------------------------
   * INITIAL LOAD
   * ---------------------------------------------------------
   */
  useEffect(() => {
    loadBilling();
    loadConnectAccount();
  }, [
    loadBilling,
    loadConnectAccount
  ]);

  /*
   * ---------------------------------------------------------
   * STRIPE RETURN HANDLING
   * ---------------------------------------------------------
   *
   * Handles:
   *
   * ?checkout=success
   * ?checkout=cancelled
   *
   * ?stripe_connect=return
   * ?stripe_connect=refresh
   */
  useEffect(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    const checkout =
      params.get('checkout');

    const stripeConnect =
      params.get(
        'stripe_connect'
      );

    if (
      !checkout &&
      !stripeConnect
    ) {
      return;
    }

    let cancelled = false;

    const handleBillingReturn =
      async () => {
        /*
         * -----------------------------------------------
         * PROFITYO SUBSCRIPTION CHECKOUT
         * -----------------------------------------------
         */
        if (
          checkout === 'success'
        ) {
          if (!cancelled) {
            setNotice({
              type: 'success',
              text: 'Checkout completed successfully. Stripe is confirming your subscription now.'
            });
          }

          await loadBilling(true);
        } else if (
          checkout === 'cancelled'
        ) {
          if (!cancelled) {
            setNotice({
              type: 'info',
              text: 'Checkout was cancelled. No subscription was activated.'
            });
          }
        }

        /*
         * -----------------------------------------------
         * STRIPE CONNECT RETURN
         * -----------------------------------------------
         */
        if (
          stripeConnect ===
          'return'
        ) {
          if (!cancelled) {
            setNotice({
              type: 'success',
              text: 'Stripe setup was submitted. Checking your account status now.'
            });

            setConnectError(null);
          }

          await loadConnectAccount(
            true
          );
        } else if (
          stripeConnect ===
          'refresh'
        ) {
          if (!cancelled) {
            setNotice({
              type: 'info',
              text: 'Your Stripe setup session expired. Please click Set up Stripe to continue.'
            });
          }
        }

        /*
         * -----------------------------------------------
         * REMOVE ONLY STRIPE RESULT PARAMETERS
         * -----------------------------------------------
         *
         * Preserve any unrelated query parameters.
         */
        params.delete('checkout');
        params.delete(
          'stripe_connect'
        );

        const remainingSearch =
          params.toString();

        if (!cancelled) {
          navigate(
            {
              pathname:
                location.pathname,
              search:
                remainingSearch
                  ? `?${remainingSearch}`
                  : ''
            },
            {
              replace: true
            }
          );
        }
      };

    handleBillingReturn();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.search,
    navigate,
    loadBilling,
    loadConnectAccount
  ]);

  /*
   * ---------------------------------------------------------
   * SUBSCRIPTION STATUS
   * ---------------------------------------------------------
   */
  const currentStatus =
    String(
      subscription?.status || ''
    ).toLowerCase();

  const currentPlanName =
    String(
      subscription?.plan_name || ''
    )
      .trim()
      .toLowerCase();

  const hasExistingSubscription =
    [
      'trialing',
      'active',
      'past_due',
      'unpaid',
      'paused'
    ].includes(currentStatus);

  const hasIncompleteSubscription =
    currentStatus ===
    'incomplete';

  const checkoutLoading =
    checkoutPlanId !== null;

  /*
   * ---------------------------------------------------------
   * STRIPE CONNECT STATUS
   * ---------------------------------------------------------
   */
  const connectStatus =
    getConnectStatusLabel(
      connectAccount
    );

  const connectStatusClasses =
    getConnectStatusClasses(
      connectAccount
    );

  const isConnectComplete =
    connectStatus === 'Connected';

  /*
   * ---------------------------------------------------------
   * PRICING
   * ---------------------------------------------------------
   */
  const selectedPriceLabel =
    useMemo(
      () =>
        billingInterval ===
        'year'
          ? 'Yearly billing'
          : 'Monthly billing',
      [billingInterval]
    );

  /*
   * ---------------------------------------------------------
   * CURRENT PLAN DISPLAY
   * ---------------------------------------------------------
   */
  const currentPlanDisplay =
    subscription?.plan_name ||
    'Free';

  const currentStatusLabel =
    getStatusLabel(
      currentStatus
    );

  const currentStatusClasses =
    getStatusClasses(
      currentStatus
    );

  /*
   * ---------------------------------------------------------
   * STRIPE CONNECT ONBOARDING
   * ---------------------------------------------------------
   */
  const handleStripeConnect =
    async () => {
      if (
        !organizationId ||
        !canManage ||
        connectLoading
      ) {
        return;
      }

      try {
        setConnectLoading(true);
        setConnectError(null);
        setNotice(null);

        const result =
          await stripeConnectService
            .createAccountLink(
              organizationId
            );

        const onboardingUrl =
          result?.onboardingUrl ||
          result?.url ||
          result?.accountLinkUrl ||
          result?.account_link_url;

        if (!onboardingUrl) {
          throw new Error(
            'Stripe did not return an onboarding URL.'
          );
        }

        let destination;

        try {
          destination =
            new URL(
              onboardingUrl
            );
        } catch {
          throw new Error(
            'Stripe returned an invalid onboarding URL.'
          );
        }

        if (
          destination.protocol !==
            'https:' &&
          destination.protocol !==
            'http:'
        ) {
          throw new Error(
            'The Stripe onboarding URL is invalid.'
          );
        }

        window.location.assign(
          destination.toString()
        );
      } catch (
        connectSetupError
      ) {
        console.error(
          'Stripe Connect setup error:',
          connectSetupError
        );

        setConnectError(
          connectSetupError?.message ||
            'Unable to start Stripe Connect setup.'
        );
      } finally {
        setConnectLoading(false);
      }
    };

  /*
   * ---------------------------------------------------------
   * CHOOSE SUBSCRIPTION PLAN
   * ---------------------------------------------------------
   */
  const handleChoosePlan =
    async (plan) => {
      if (
        !plan ||
        plan.is_free ||
        !canManage ||
        checkoutLoading
      ) {
        return;
      }

      if (
        hasExistingSubscription
      ) {
        setNotice({
          type: 'info',
          text: 'This organization already has an active subscription.'
        });

        return;
      }

      if (
        hasIncompleteSubscription
      ) {
        setNotice({
          type: 'info',
          text: 'This organization already has a pending checkout. Please complete that checkout or refresh the page before starting another one.'
        });

        return;
      }

      if (!organizationId) {
        setError(
          'No managing organization is available for billing.'
        );

        return;
      }

      try {
        setCheckoutPlanId(
          plan.id
        );

        setError(null);
        setNotice(null);

        const result =
          await subscriptionService
            .createCheckoutSession({
              organizationId,
              planId: plan.id,
              billingInterval
            });

        if (
          !result?.checkoutUrl
        ) {
          throw new Error(
            'Stripe Checkout did not return a checkout URL.'
          );
        }

        window.location.href =
          result.checkoutUrl;
      } catch (
        checkoutError
      ) {
        console.error(
          'Checkout error:',
          checkoutError
        );

        if (
          checkoutError?.code ===
          'SUBSCRIPTION_EXISTS'
        ) {
          setNotice({
            type: 'info',
            text: 'This organization already has a subscription.'
          });

          await loadBilling(
            true
          );
        } else if (
          checkoutError?.code ===
          'FREE_PLAN'
        ) {
          setNotice({
            type: 'info',
            text: 'The Free plan does not require Stripe Checkout.'
          });
        } else {
          setError(
            checkoutError?.message ||
              'Unable to start checkout.'
          );
        }
      } finally {
        setCheckoutPlanId(null);
      }
    };

  /*
   * ---------------------------------------------------------
   * INITIAL LOADING
   * ---------------------------------------------------------
   */
  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-180px)] items-center justify-center px-6">
        <div className="flex w-full max-w-sm flex-col items-center rounded-[32px] border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/10">
            <SafeIcon
              icon={FiCreditCard}
              className="text-xl"
            />
          </div>

          <div className="mb-4 flex items-center gap-2">
            <SafeIcon
              icon={FiLoader}
              className="animate-spin text-blue-600"
            />

            <span className="text-sm font-bold text-slate-700">
              Loading billing
            </span>
          </div>

          <p className="text-xs leading-5 text-slate-400">
            Checking your plans and current subscription.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50/40">
      <div className="mx-auto max-w-[1180px] px-4 py-8 sm:px-6 lg:px-8">

        {/* Header */}

        <div className="mb-8 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
          <div className="relative px-6 py-7 md:px-8">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-100/60 blur-3xl" />

            <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/10">
                  <SafeIcon
                    icon={FiCreditCard}
                    className="text-lg"
                  />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-black tracking-tight text-slate-900">
                      Billing
                    </h1>

                    {organizationId && (
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${currentStatusClasses}`}
                      >
                        {currentStatusLabel}
                      </span>
                    )}
                  </div>

                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                    Manage your ProfitYO subscription and payment setup for your organization.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={async () => {
                  await Promise.all([
                    loadBilling(true),
                    loadConnectAccount(
                      true
                    )
                  ]);
                }}
                disabled={
                  refreshing ||
                  connectRefreshing ||
                  checkoutLoading ||
                  connectLoading
                }
                className="group inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SafeIcon
                  icon={FiRefreshCw}
                  className={
                    refreshing ||
                    connectRefreshing
                      ? 'animate-spin'
                      : 'transition-transform group-hover:rotate-45'
                  }
                />

                {refreshing ||
                connectRefreshing
                  ? 'Refreshing...'
                  : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Organization warning */}

        {!organizationId && (
          <div className="mb-8 rounded-[28px] border border-amber-200 bg-amber-50 p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <SafeIcon
                  icon={FiAlertCircle}
                />
              </div>

              <div>
                <h2 className="font-black text-amber-900">
                  Billing organization unavailable
                </h2>

                <p className="mt-1 text-sm leading-6 text-amber-800">
                  No managing accounting organization is currently available for billing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notice */}

        {notice && (
          <div
            className={`mb-6 overflow-hidden rounded-2xl border shadow-sm ${
              notice.type ===
              'success'
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-blue-200 bg-blue-50'
            }`}
          >
            <div className="flex items-start gap-3 p-4">
              <SafeIcon
                icon={
                  notice.type ===
                  'success'
                    ? FiCheckCircle
                    : FiAlertCircle
                }
                className={`mt-0.5 shrink-0 ${
                  notice.type ===
                  'success'
                    ? 'text-emerald-600'
                    : 'text-blue-600'
                }`}
              />

              <p
                className={`text-sm font-semibold leading-6 ${
                  notice.type ===
                  'success'
                    ? 'text-emerald-800'
                    : 'text-blue-800'
                }`}
              >
                {notice.text}
              </p>
            </div>
          </div>
        )}

        {/* Error */}

        {error && (
          <div className="mb-6 overflow-hidden rounded-2xl border border-rose-200 bg-rose-50 shadow-sm">
            <div className="flex items-start gap-3 p-4">
              <SafeIcon
                icon={FiAlertCircle}
                className="mt-0.5 shrink-0 text-rose-600"
              />

              <div className="min-w-0">
                <p className="text-sm font-black text-rose-900">
                  Unable to continue
                </p>

                <p className="mt-1 break-words text-sm font-medium leading-6 text-rose-700">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {organizationId && (
          <>
            {/* Stripe Connect */}

            <section className="mb-8 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
              <div className="p-6 md:p-8">
                <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">

                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-50 text-violet-700">
                      <SafeIcon
                        icon={FiLink}
                        className="text-lg"
                      />
                    </div>

                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-lg font-black tracking-tight text-slate-900">
                          Stripe Connect
                        </h2>

                        <span
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${connectStatusClasses}`}
                        >
                          {connectStatus}
                        </span>
                      </div>

                      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                        Connect your organization's Stripe account so you can send invoices to customers and receive payments through ProfitYO.
                      </p>

                      {connectAccount
                        ?.stripe_account_id && (
                        <p className="mt-2 text-xs font-semibold text-slate-400">
                          Stripe account:{' '}
                          {
                            connectAccount
                              .stripe_account_id
                          }
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0">
                    {!isConnectComplete ? (
                      <button
                        type="button"
                        onClick={
                          handleStripeConnect
                        }
                        disabled={
                          !canManage ||
                          connectLoading
                        }
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-xs font-bold text-white shadow-sm transition-all hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {connectLoading ? (
                          <>
                            <SafeIcon
                              icon={FiLoader}
                              className="animate-spin"
                            />

                            Opening Stripe...
                          </>
                        ) : (
                          <>
                            <SafeIcon
                              icon={FiLink}
                            />

                            Set up Stripe

                            <SafeIcon
                              icon={
                                FiExternalLink
                              }
                            />
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-xs font-bold text-emerald-700">
                        <SafeIcon
                          icon={
                            FiCheckCircle
                          }
                        />

                        Stripe connected
                      </div>
                    )}
                  </div>
                </div>

                {connectError && (
                  <div className="mt-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4">
                    <SafeIcon
                      icon={
                        FiAlertCircle
                      }
                      className="mt-0.5 shrink-0 text-rose-600"
                    />

                    <div>
                      <p className="text-xs font-black text-rose-900">
                        Stripe Connect setup failed
                      </p>

                      <p className="mt-1 break-words text-xs leading-5 text-rose-700">
                        {connectError}
                      </p>
                    </div>
                  </div>
                )}

                {!canManage && (
                  <div className="mt-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <SafeIcon
                      icon={FiShield}
                      className="mt-0.5 shrink-0 text-slate-500"
                    />

                    <div>
                      <p className="text-xs font-black text-slate-700">
                        Stripe setup is restricted
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        Only organization owners and owner members can manage Stripe Connect.
                      </p>
                    </div>
                  </div>
                )}

                {isConnectComplete && (
                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Account
                      </p>

                      <p className="mt-1 text-sm font-bold text-slate-800">
                        Connected
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Payments
                      </p>

                      <p className="mt-1 text-sm font-bold text-emerald-700">
                        Enabled
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Payouts
                      </p>

                      <p className="mt-1 text-sm font-bold text-emerald-700">
                        Enabled
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Current subscription */}

            <section className="mb-8 overflow-hidden rounded-[32px] border border-slate-200 bg-slate-900 text-white shadow-xl shadow-slate-900/10">
              <div className="relative p-6 md:p-8">
                <div className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

                <div className="pointer-events-none absolute -bottom-32 left-1/3 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />

                <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <SafeIcon
                        icon={FiZap}
                        className="text-blue-300"
                      />

                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                        Current subscription
                      </span>
                    </div>

                    <div className="flex flex-wrap items-end gap-3">
                      <h2 className="text-3xl font-black tracking-tight">
                        {currentPlanDisplay}
                      </h2>

                      <span
                        className={`mb-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
                          currentStatus ===
                            'active' ||
                          currentStatus ===
                            'trialing'
                            ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
                            : 'border-white/10 bg-white/5 text-slate-300'
                        }`}
                      >
                        {currentStatusLabel}
                      </span>
                    </div>

                    <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">
                      Your ProfitYO subscription is managed at the accounting organization level and applies to the organizations it manages.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:min-w-[390px]">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="mb-2 text-slate-400">
                        <SafeIcon
                          icon={
                            FiCreditCard
                          }
                        />
                      </div>

                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        Billing
                      </p>

                      <p className="mt-1 text-sm font-bold text-white">
                        {subscription
                          ?.billing_interval ===
                        'year'
                          ? 'Yearly'
                          : subscription
                              ?.billing_interval ===
                            'month'
                          ? 'Monthly'
                          : '—'}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="mb-2 text-slate-400">
                        <SafeIcon
                          icon={FiShield}
                        />
                      </div>

                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        Currency
                      </p>

                      <p className="mt-1 text-sm font-bold text-white">
                        {currency}
                      </p>
                    </div>

                    <div className="col-span-2 rounded-2xl border border-white/10 bg-white/5 p-4 sm:col-span-1">
                      <div className="mb-2 text-slate-400">
                        <SafeIcon
                          icon={FiUsers}
                        />
                      </div>

                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        Organization
                      </p>

                      <p className="mt-1 truncate text-sm font-bold text-white">
                        {activeClient?.name ||
                          'Organization'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <BillingSubscriptionCard
              subscription={
                subscription
              }
            />

            {/* Plans */}

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm md:p-7">
              <div className="mb-7 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />

                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                      Plans & pricing
                    </span>
                  </div>

                  <h2 className="text-2xl font-black tracking-tight text-slate-900">
                    Choose your plan
                  </h2>

                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                    Select the subscription that matches your organization's accounting needs.
                  </p>
                </div>

                <div className="flex w-full rounded-2xl border border-slate-200 bg-slate-100 p-1 sm:w-auto">
                  {[
                    {
                      value: 'month',
                      label: 'Monthly'
                    },
                    {
                      value: 'year',
                      label: 'Yearly'
                    }
                  ].map(
                    (option) => {
                      const selected =
                        billingInterval ===
                        option.value;

                      return (
                        <button
                          key={
                            option.value
                          }
                          type="button"
                          onClick={() =>
                            setBillingInterval(
                              option.value
                            )
                          }
                          disabled={
                            checkoutLoading
                          }
                          className={`flex-1 rounded-xl px-5 py-2.5 text-xs font-black transition-all sm:flex-none ${
                            selected
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-500 hover:text-slate-900'
                          } disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                          {
                            option.label
                          }

                          {option.value ===
                            'year' && (
                            <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] text-emerald-700">
                              Save
                            </span>
                          )}
                        </button>
                      );
                    }
                  )}
                </div>
              </div>

              <div className="mb-5 flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                  {
                    selectedPriceLabel
                  }
                </p>

                {checkoutLoading && (
                  <div className="flex items-center gap-2 text-xs font-semibold text-blue-600">
                    <SafeIcon
                      icon={FiLoader}
                      className="animate-spin"
                    />

                    Connecting to Stripe...
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-4">
                {plans.map(
                  (plan) => {
                    const planName =
                      String(
                        plan.name ||
                          ''
                      )
                        .trim()
                        .toLowerCase();

                    const isCurrent =
                      Boolean(
                        currentPlanName
                      ) &&
                      planName ===
                        currentPlanName;

                    const isLoading =
                      checkoutPlanId ===
                      plan.id;

                    return (
                      <BillingPlanCard
                        key={plan.id}
                        plan={plan}
                        billingInterval={
                          billingInterval
                        }
                        currency={
                          currency
                        }
                        isCurrent={
                          isCurrent
                        }
                        canManage={
                          canManage &&
                          !hasExistingSubscription &&
                          !hasIncompleteSubscription
                        }
                        loading={
                          isLoading
                        }
                        checkoutInProgress={
                          checkoutLoading
                        }
                        onChoose={
                          handleChoosePlan
                        }
                      />
                    );
                  }
                )}
              </div>

              {!canManage && (
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <SafeIcon
                    icon={FiShield}
                    className="mt-0.5 shrink-0 text-slate-500"
                  />

                  <div>
                    <p className="text-xs font-black text-slate-700">
                      Billing access is restricted
                    </p>

                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Only organization owners and owner members can manage subscriptions.
                    </p>
                  </div>
                </div>
              )}

              {hasExistingSubscription && (
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                  <SafeIcon
                    icon={
                      FiCheckCircle
                    }
                    className="mt-0.5 shrink-0 text-blue-600"
                  />

                  <div>
                    <p className="text-xs font-black text-blue-800">
                      Subscription already active
                    </p>

                    <p className="mt-1 text-xs leading-5 text-blue-700">
                      This organization already has a Stripe subscription. Plan changes should be handled through the subscription management workflow.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-col justify-between gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
                  <SafeIcon
                    icon={FiShield}
                    className="text-emerald-500"
                  />

                  Secure payments powered by Stripe
                </div>

                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                  <span>
                    Need help?
                  </span>

                  <span className="font-bold text-slate-600">
                    Contact your administrator
                  </span>

                  <SafeIcon
                    icon={
                      FiArrowRight
                    }
                    className="text-slate-400"
                  />
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}